import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ProductClass, TradeDirection, ServicesClass } from "@atlas-int/data/types";
import type { UIView } from "@atlas-int/data";
import {
  TooltipLineMarker,
  TooltipStateBridge,
  useIsolatedTooltipPayload,
  useTooltipDispatchRef,
  VisualizationContent,
  GraphNoticeType,
  type TooltipDatum,
} from "@atlas-int/viz/shared";
import { TooltipContent } from "../components/tooltip/TooltipContent";
import { VisualizationContentContainer } from "../VizGrid";
import { MarketShareChart as Chart } from "@atlas-int/viz/marketshare";
import type { MarketShareDataPoint } from "@atlas-int/viz/marketshare";
import { dataVizLayout } from "@atlas-int/tokens";
const defaultChartMargin = dataVizLayout.defaultChartMargin;

const chartMargin = { ...defaultChartMargin, bottom: 50 };

interface XAxisTooltipValue {
  year: number | undefined;
  x: number | undefined;
  y: number | undefined;
}

const MarketShareTooltipContent = memo(function MarketShareTooltipContent({
  view,
  totalValue,
  tradeDirection,
  productClass,
  loading,
}: {
  view: UIView;
  totalValue: number | undefined;
  tradeDirection: TradeDirection;
  productClass: ProductClass;
  loading: boolean;
}) {
  const tooltipState = useIsolatedTooltipPayload();
  if (!tooltipState?.contentData || loading) return null;
  return (
    <TooltipContent
      datum={tooltipState.contentData as TooltipDatum}
      view={view}
      totalValue={totalValue}
      tradeDirection={tradeDirection}
      productClass={productClass}
    />
  );
});

const MarketShareChartMemo = memo(function MarketShareChartMemo({
  successResponse,
  exporter,
  yearMin,
  yearMax,
  productClass,
  servicesClass,
  hiddenCategories,
  currentTradeDirection,
  setXAxisTooltipValue,
  handleTooltip,
  isTooltipFixed,
}: {
  successResponse: MarketShareWithTooltipProps["successResponse"];
  exporter: string;
  yearMin: number;
  yearMax: number;
  productClass: ProductClass;
  servicesClass: ServicesClass;
  hiddenCategories: string[];
  currentTradeDirection: TradeDirection;
  setXAxisTooltipValue: (v: XAxisTooltipValue) => void;
  handleTooltip: (params: { data: MarketShareDataPoint[] }) => void;
  isTooltipFixed: boolean;
}) {
  if (!successResponse) return null;
  const queryVariables = useMemo(
    () => ({ exporter, yearMin, yearMax, productClass, servicesClass }),
    [exporter, yearMin, yearMax, productClass, servicesClass],
  );
  return (
    <Chart
      inputData={successResponse}
      queryVariables={queryVariables}
      hiddenCategories={hiddenCategories}
      tradeDirection={currentTradeDirection}
      setXAxisTooltipValue={setXAxisTooltipValue}
      handleTooltip={handleTooltip}
      chartMargin={chartMargin}
      isTooltipFixed={isTooltipFixed}
    />
  );
});

const MarketShareLineMarker = memo(function MarketShareLineMarker({
  loading,
  containerHeight,
  isTooltipFixed,
  fixedXAxisTooltipValue,
  xAxisTooltipValue,
}: {
  loading: boolean;
  containerHeight: number;
  isTooltipFixed: boolean;
  fixedXAxisTooltipValue: XAxisTooltipValue | null;
  xAxisTooltipValue: XAxisTooltipValue;
}) {
  const tooltipState = useIsolatedTooltipPayload();
  if (!tooltipState?.contentData || loading || !containerHeight) return null;

  const tooltipXAxisValue =
    isTooltipFixed && fixedXAxisTooltipValue
      ? fixedXAxisTooltipValue
      : xAxisTooltipValue;

  if (!tooltipXAxisValue) return null;

  const { year, x } = tooltipXAxisValue;
  return (
    <TooltipLineMarker
      year={year}
      x={x}
      yAxisMarkerExtent={{
        min: containerHeight,
        max: chartMargin.top,
      }}
      chartMargin={chartMargin}
      containerHeight={containerHeight}
    />
  );
});

export interface MarketShareWithTooltipProps {
  chartContainerRef: React.RefObject<HTMLDivElement | null>;
  successResponse: { data: Record<string, unknown>[] } | null | undefined;
  exporter: string;
  yearMin: number;
  yearMax: number;
  productClass: ProductClass;
  servicesClass: ServicesClass;
  hiddenCategories: string[];
  currentTradeDirection: TradeDirection;
  view: UIView;
  loading: boolean;
  hasError: boolean;
  errorNoticeType?: GraphNoticeType;
  hasNoData: boolean;
}

/**
 * Tooltip, line marker, and chart. Uses {@link TooltipStateIsolationProvider} from the parent;
 * hover payload updates rerender this subtree only, not ConnectedMarketShare.
 */
const MarketShareWithTooltip = memo(function MarketShareWithTooltip({
  chartContainerRef,
  successResponse,
  exporter,
  yearMin,
  yearMax,
  productClass,
  servicesClass,
  hiddenCategories,
  currentTradeDirection,
  view,
  loading,
  hasError,
  errorNoticeType,
  hasNoData,
}: MarketShareWithTooltipProps) {
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  const [chartContainerOffset, setChartContainerOffset] = useState<
    { left: number; top: number } | undefined
  >(undefined);

  const dispatchRef = useTooltipDispatchRef();
  const tooltipState = useIsolatedTooltipPayload<TooltipDatum>();

  const isTooltipFixed = tooltipState?.isFixed ?? false;

  const totalValue = undefined;

  const [xAxisTooltipValue, setXAxisTooltipValue] =
    useState<XAxisTooltipValue>({
      year: undefined,
      x: undefined,
      y: undefined,
    });
  const [fixedXAxisTooltipValue, setFixedXAxisTooltipValue] =
    useState<XAxisTooltipValue | null>(null);

  const xAxisTooltipValueRef = useRef(xAxisTooltipValue);
  xAxisTooltipValueRef.current = xAxisTooltipValue;

  const isTooltipFixedRef = useRef(isTooltipFixed);
  isTooltipFixedRef.current = isTooltipFixed;

  const onHover = useCallback(
    ({ data }: { data: MarketShareDataPoint[] }) => {
      if (isTooltipFixedRef.current) {
        return;
      }

      if (!data || data.length === 0) {
        dispatchRef.current.hideTooltip();
        return;
      }

      const currentXAxisValue = xAxisTooltipValueRef.current;
      const filteredData = data.filter(
        (d) => d.year === currentXAxisValue?.year,
      );

      if (!filteredData || filteredData.length === 0) {
        dispatchRef.current.hideTooltip();
        return;
      }

      const mappedDatum: TooltipDatum = {
        kind: "marketShare" as const,
        year: currentXAxisValue?.year as number,
        dataForYear: filteredData,
      };

      dispatchRef.current.showTooltip(mappedDatum);
    },
    [dispatchRef],
  );

  const snapTooltipToCoordinates = useMemo(() => {
    let snapCoordinates: [number, number] | undefined;

    const tooltipXAxisValue =
      isTooltipFixed && fixedXAxisTooltipValue
        ? fixedXAxisTooltipValue
        : xAxisTooltipValue;

    if (
      tooltipXAxisValue &&
      chartContainerOffset &&
      containerSize.height > 0
    ) {
      const { x } = tooltipXAxisValue;
      const { left, top } = chartContainerOffset;
      snapCoordinates = [
        (x ?? 0) + left,
        chartMargin.top +
          top +
          (containerSize.height - chartMargin.bottom - chartMargin.top) / 2,
      ];
    }
    return snapCoordinates;
  }, [
    chartContainerOffset,
    containerSize.height,
    isTooltipFixed,
    fixedXAxisTooltipValue,
    xAxisTooltipValue,
  ]);

  useEffect(() => {
    if (isTooltipFixed && xAxisTooltipValue) {
      setFixedXAxisTooltipValue(xAxisTooltipValue);
    } else if (!isTooltipFixed) {
      setFixedXAxisTooltipValue(null);
    }
  }, [isTooltipFixed, xAxisTooltipValue]);

  useEffect(() => {
    const node = chartContainerRef.current;
    if (!node) return;

    const updateOffset = () => {
      const { left, top } = node.getBoundingClientRect();
      setChartContainerOffset((prev) => {
        if (
          !prev ||
          Math.abs(prev.left - left) > 0.5 ||
          Math.abs(prev.top - top) > 0.5
        ) {
          return { left, top };
        }
        return prev;
      });
    };

    const updateSize = () => {
      const width = node.clientWidth;
      const height = node.clientHeight - 10;
      setContainerSize((prev) => {
        if (prev.width !== width || prev.height !== height) {
          return { width, height };
        }
        return prev;
      });
    };

    updateOffset();
    updateSize();

    const resizeObserver = new ResizeObserver(() => {
      updateOffset();
      updateSize();
    });
    resizeObserver.observe(node);

    window.addEventListener("scroll", updateOffset, true);
    window.addEventListener("resize", updateOffset);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", updateOffset, true);
      window.removeEventListener("resize", updateOffset);
    };
  }, [chartContainerRef]);

  const handleFixedChange = useCallback(
    (fixed: boolean) => {
      if (!fixed) {
        dispatchRef.current.clearFixed();
      }
    },
    [dispatchRef],
  );

  const clearHighlightedItem = useCallback(() => {
    if (isTooltipFixedRef.current) {
      dispatchRef.current.clearFixed();
    }
  }, [dispatchRef]);

  return (
    <TooltipStateBridge
      tooltipContent={
        <MarketShareTooltipContent
          view={view}
          totalValue={totalValue}
          tradeDirection={currentTradeDirection}
          productClass={productClass}
          loading={loading}
        />
      }
      cursor="default"
      overrideStyles={true}
      clearHighlightedItem={clearHighlightedItem}
      isFixed={isTooltipFixed}
      onFixedChange={handleFixedChange}
      isFixedCoords={snapTooltipToCoordinates}
      snapToPosition={true}
      deriveTooltipPropsFromState={false}
      portalContainerId="overlayPortalContainerId"
    >
      <VisualizationContentContainer ref={chartContainerRef}>
        <VisualizationContent
          hasError={hasError}
          errorNoticeType={errorNoticeType}
          hasNoData={hasNoData}
          loading={loading}
        >
          <MarketShareChartMemo
            successResponse={successResponse}
            exporter={exporter}
            yearMin={yearMin}
            yearMax={yearMax}
            productClass={productClass}
            servicesClass={servicesClass}
            hiddenCategories={hiddenCategories}
            currentTradeDirection={currentTradeDirection}
            setXAxisTooltipValue={setXAxisTooltipValue}
            handleTooltip={onHover}
            isTooltipFixed={isTooltipFixed}
          />
          {!loading && (
            <MarketShareLineMarker
              loading={loading}
              containerHeight={containerSize.height}
              isTooltipFixed={isTooltipFixed}
              fixedXAxisTooltipValue={fixedXAxisTooltipValue}
              xAxisTooltipValue={xAxisTooltipValue}
            />
          )}
        </VisualizationContent>
      </VisualizationContentContainer>
    </TooltipStateBridge>
  );
});

export default MarketShareWithTooltip;
